import React, { useState } from 'react';
import './Dashboard.css';
import DepositManager from './DepositManager';
import ApiService from '../services/api';

const Dashboard = ({ portfolioData, botRunning, isConnectedToBot, botData }) => {
  const [showDepositManager, setShowDepositManager] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatPercentage = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const getPnLColor = (pnl) => {
    if (pnl > 0) return '#00ff88';
    if (pnl < 0) return '#ff4757';
    return '#ffffff';
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>📊 Dashboard de Trading</h2>
        <div className="connection-info">
          <div className="last-update">
            Dernière mise à jour: {new Date().toLocaleTimeString('fr-FR')}
          </div>
          <div className={`bot-connection-status ${isConnectedToBot ? 'connected' : 'disconnected'}`}>
            <span className="status-dot"></span>
            {isConnectedToBot ? '🤖 Bot Python Connecté' : '📊 Mode Simulation'}
          </div>
        </div>
      </div>

      <div className="metrics-grid">
        <div className="metric-card balance">
          <div className="metric-header">
            <span className="metric-icon">💰</span>
            <span className="metric-title">Balance Totale</span>
          </div>
          <div className="metric-value">
            {formatCurrency(portfolioData.balance)}
          </div>
          <div className="metric-subtitle">
            Liquidités disponibles
          </div>
        </div>

        <div className="metric-card pnl">
          <div className="metric-header">
            <span className="metric-icon">📈</span>
            <span className="metric-title">PnL Total</span>
          </div>
          <div 
            className="metric-value"
            style={{ color: getPnLColor(portfolioData.pnl) }}
          >
            {formatCurrency(portfolioData.pnl)}
          </div>
          <div className="metric-subtitle">
            {formatPercentage((portfolioData.pnl / portfolioData.balance) * 100)}
          </div>
        </div>

        <div className="metric-card trades">
          <div className="metric-header">
            <span className="metric-icon">🔄</span>
            <span className="metric-title">Trades Totaux</span>
          </div>
          <div className="metric-value">
            {portfolioData?.totalTrades || 0}
          </div>
          <div className="metric-subtitle">
            Exécutés aujourd'hui
          </div>
        </div>

        <div className="metric-card winrate">
          <div className="metric-header">
            <span className="metric-icon">🎯</span>
            <span className="metric-title">Taux de Réussite</span>
          </div>
          <div className="metric-value">
            {formatPercentage(portfolioData?.winRate || 0)}
          </div>
          <div className="metric-subtitle">
            Trades gagnants
          </div>
        </div>
      </div>

      {/* Section spéciale pour les données du bot Python */}
      {isConnectedToBot && botData && (
        <div className="bot-data-section">
          <h3>🤖 Données Bot Python Temps Réel</h3>
          <div className="bot-metrics-grid">
            <div className="bot-metric">
              <span className="bot-metric-label">📊 DCA Effectués:</span>
              <span className="bot-metric-value">{botData.dca_count || 0}/20</span>
            </div>
            <div className="bot-metric">
              <span className="bot-metric-label">⚡ Micro-DCA:</span>
              <span className="bot-metric-value">{botData.micro_dca_count || 0}/6</span>
            </div>
            <div className="bot-metric">
              <span className="bot-metric-label">🎯 Régime Marché:</span>
              <span className="bot-metric-value">{(botData.market_regime || 'normal').toUpperCase()}</span>
            </div>
            <div className="bot-metric">
              <span className="bot-metric-label">📈 Volatilité:</span>
              <span className="bot-metric-value">{(botData.volatility || 0).toFixed(2)}%</span>
            </div>
            {botData.position && (
              <>
                <div className="bot-metric">
                  <span className="bot-metric-label">📍 Position SOL:</span>
                  <span className="bot-metric-value">{botData.position.size?.toFixed(3)} SOL</span>
                </div>
                <div className="bot-metric">
                  <span className="bot-metric-label">💰 Prix Entrée:</span>
                  <span className="bot-metric-value">${botData.position.entry_price?.toFixed(2)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="status-section">
        <div className="trading-status">
          <h3>🤖 Statut du Trading</h3>
          <div className="status-grid">
            <div className="status-item">
              <span className="status-label">Bot:</span>
              <span className={`status-value ${botRunning ? 'active' : 'inactive'}`}>
                {botRunning ? '🟢 ACTIF' : '🔴 INACTIF'}
              </span>
            </div>
            
            <div className="status-item">
              <span className="status-label">Stratégie:</span>
              <span className="status-value">DCA V3.6 GODMODE</span>
            </div>
            
            <div className="status-item">
              <span className="status-label">DCA Niveaux:</span>
              <span className="status-value">Tous les -3%</span>
            </div>
            
            <div className="status-item">
              <span className="status-label">Take Profit:</span>
              <span className="status-value">+1.5%</span>
            </div>
          </div>
        </div>

        <div className="recent-activity">
          <h3>📝 Activité Récente</h3>
          <div className="activity-list">
            <div className="activity-item">
              <span className="activity-time">12:34:56</span>
              <span className="activity-text">Position surveillée - PnL: +0.2%</span>
            </div>
            <div className="activity-item">
              <span className="activity-time">12:34:53</span>
              <span className="activity-text">Cycle de vérification terminé</span>
            </div>
            <div className="activity-item">
              <span className="activity-time">12:34:50</span>
              <span className="activity-text">Analyse RSI: 52.3 (Neutre)</span>
            </div>
            <div className="activity-item">
              <span className="activity-time">12:34:47</span>
              <span className="activity-text">Synchronisation compteurs OK</span>
            </div>
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <h3>⚡ Actions Rapides</h3>
        <div className="actions-grid">
          <button 
            className="action-btn deposit-btn"
            onClick={() => setShowDepositManager(true)}
          >
            💸 Déposer USDC
          </button>
          <button className="action-btn view-logs">
            📋 Voir les Logs
          </button>
          <button className="action-btn view-history">
            📊 Historique
          </button>
          <button className="action-btn settings">
            ⚙️ Paramètres
          </button>
        </div>
      </div>

      {/* Modal de dépôt */}
      {showDepositManager && (
        <DepositManager onClose={() => setShowDepositManager(false)} />
      )}
    </div>
  );
};

export default Dashboard;
